// Configuration des questions initiales par type de projet

export type ProjectType =
  | 'site_vitrine'
  | 'ecommerce'
  | 'application_web'
  | 'application_mobile'
  | 'refonte'
  | 'maintenance'
  | 'autre'

export type QuestionType =
  | 'text'
  | 'textarea'
  | 'multiple_choice'
  | 'checkbox_multiple'
  | 'yes_no'
  | 'open'

export interface ProjectQuestion {
  id: string
  type: QuestionType
  question: string
  placeholder?: string
  options?: string[]
  required?: boolean
  category?: string
}

// Questions initiales par type de projet (5 questions de base)
export const INITIAL_QUESTIONS: Record<ProjectType, ProjectQuestion[]> = {
  site_vitrine: [
    {
      id: 'company_description',
      type: 'textarea',
      question: 'Decrivez votre entreprise et votre activite',
      placeholder: 'Ex: Nous sommes une entreprise de conseil en marketing digital...',
      required: true,
      category: 'general',
    },
    {
      id: 'main_objective',
      type: 'multiple_choice',
      question: 'Quel est l\'objectif principal de votre site ?',
      options: [
        'Presenter mes services/produits',
        'Generer des prospects',
        'Ameliorer ma visibilite en ligne',
        'Vendre en ligne',
        'Autre',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'target_audience',
      type: 'textarea',
      question: 'Qui est votre public cible ?',
      placeholder: 'Ex: Entreprises B2B, particuliers de 25-45 ans...',
      required: true,
      category: 'general',
    },
    {
      id: 'essential_pages',
      type: 'checkbox_multiple',
      question: 'Quelles pages sont essentielles pour votre site ?',
      options: [
        'Accueil',
        'A propos',
        'Services/Produits',
        'Portfolio/Realisations',
        'Blog',
        'Contact',
        'FAQ',
        'Autre',
      ],
      required: true,
      category: 'content',
    },
    {
      id: 'visual_style',
      type: 'multiple_choice',
      question: 'Quel style visuel preferez-vous ?',
      options: [
        'Moderne et minimaliste',
        'Elegant et professionnel',
        'Creatif et original',
        'Classique et serieux',
        'Je ne sais pas',
      ],
      required: false,
      category: 'design',
    },
  ],
  ecommerce: [
    {
      id: 'products_description',
      type: 'textarea',
      question: 'Quels types de produits allez-vous vendre ?',
      placeholder: 'Ex: Vetements pour enfants, accessoires de mode...',
      required: true,
      category: 'general',
    },
    {
      id: 'product_count',
      type: 'multiple_choice',
      question: 'Combien de produits prevoyez-vous de vendre ?',
      options: [
        'Moins de 50',
        '50 a 200',
        '200 a 500',
        'Plus de 500',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'payment_methods',
      type: 'checkbox_multiple',
      question: 'Quels moyens de paiement souhaitez-vous proposer ?',
      options: [
        'Carte bancaire',
        'PayPal',
        'Virement bancaire',
        'Paiement a la livraison',
        'Autres',
      ],
      required: true,
      category: 'features',
    },
    {
      id: 'shipping_zones',
      type: 'multiple_choice',
      question: 'Dans quelles zones livrez-vous ?',
      options: [
        'France uniquement',
        'Europe',
        'International',
        'Retrait en magasin uniquement',
      ],
      required: true,
      category: 'features',
    },
    {
      id: 'special_features',
      type: 'checkbox_multiple',
      question: 'Fonctionnalites speciales souhaitees ?',
      options: [
        'Programme de fidelite',
        'Codes promo',
        'Wishlist',
        'Avis clients',
        'Comparateur de produits',
        'Recommandations personnalisees',
      ],
      required: false,
      category: 'features',
    },
  ],
  application_web: [
    {
      id: 'app_purpose',
      type: 'textarea',
      question: 'A quoi servira votre application web ?',
      placeholder: 'Ex: Gestion de planning pour les equipes, plateforme de reservation...',
      required: true,
      category: 'general',
    },
    {
      id: 'main_users',
      type: 'textarea',
      question: 'Qui utilisera cette application ?',
      placeholder: 'Ex: Employes internes, clients externes, les deux...',
      required: true,
      category: 'general',
    },
    {
      id: 'main_features',
      type: 'textarea',
      question: 'Quelles sont les fonctionnalites principales ?',
      placeholder: 'Ex: Creation de taches, attribution aux membres, suivi du temps...',
      required: true,
      category: 'features',
    },
    {
      id: 'user_roles',
      type: 'multiple_choice',
      question: 'Avez-vous besoin de differents roles utilisateurs ?',
      options: [
        'Oui, plusieurs roles avec permissions',
        'Non, tous les utilisateurs sont egaux',
        'Je ne sais pas',
      ],
      required: true,
      category: 'features',
    },
    {
      id: 'integrations',
      type: 'textarea',
      question: 'Quelles integrations sont necessaires ?',
      placeholder: 'Ex: Google Calendar, Stripe, CRM existant...',
      required: false,
      category: 'technical',
    },
  ],
  application_mobile: [
    {
      id: 'mobile_app_purpose',
      type: 'textarea',
      question: 'A quoi servira votre application mobile ?',
      placeholder: 'Ex: Application de fitness, jeu mobile, outil de productivite...',
      required: true,
      category: 'general',
    },
    {
      id: 'target_platforms',
      type: 'checkbox_multiple',
      question: 'Sur quelles plateformes ?',
      options: [
        'iOS (iPhone/iPad)',
        'Android',
        'Les deux',
      ],
      required: true,
      category: 'technical',
    },
    {
      id: 'mobile_features',
      type: 'checkbox_multiple',
      question: 'Quelles fonctionnalites mobiles ?',
      options: [
        'Geolocalisation',
        'Notifications push',
        'Camera/Photos',
        'Paiement in-app',
        'Mode hors-ligne',
        'Partage sur reseaux sociaux',
      ],
      required: false,
      category: 'features',
    },
    {
      id: 'monetization',
      type: 'multiple_choice',
      question: 'Modele de monetisation ?',
      options: [
        'Application gratuite',
        'Application payante',
        'Freemium (gratuit avec achats in-app)',
        'Abonnement',
        'Publicites',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'backend_needed',
      type: 'yes_no',
      question: 'Avez-vous besoin d\'un backend/serveur ?',
      required: true,
      category: 'technical',
    },
  ],
  refonte: [
    {
      id: 'current_site_url',
      type: 'text',
      question: 'Quelle est l\'URL de votre site actuel ?',
      placeholder: 'https://www.example.com',
      required: true,
      category: 'general',
    },
    {
      id: 'refonte_reasons',
      type: 'checkbox_multiple',
      question: 'Pourquoi souhaitez-vous refondre votre site ?',
      options: [
        'Design obsolete',
        'Non responsive (mobile)',
        'Mauvais referencement SEO',
        'Trop lent',
        'Difficile a mettre a jour',
        'Ajouter de nouvelles fonctionnalites',
        'Autre',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'keep_content',
      type: 'yes_no',
      question: 'Souhaitez-vous conserver le contenu actuel ?',
      required: true,
      category: 'content',
    },
    {
      id: 'new_features',
      type: 'textarea',
      question: 'Quelles nouvelles fonctionnalites voulez-vous ajouter ?',
      placeholder: 'Ex: Blog, espace client, formulaire de contact ameliore...',
      required: false,
      category: 'features',
    },
    {
      id: 'competitors',
      type: 'textarea',
      question: 'Y a-t-il des sites concurrents que vous appreciez ?',
      placeholder: 'URLs ou descriptions...',
      required: false,
      category: 'design',
    },
  ],
  maintenance: [
    {
      id: 'site_to_maintain_url',
      type: 'text',
      question: 'URL du site a maintenir ?',
      placeholder: 'https://www.example.com',
      required: true,
      category: 'general',
    },
    {
      id: 'maintenance_type',
      type: 'checkbox_multiple',
      question: 'Quel type de maintenance souhaitez-vous ?',
      options: [
        'Mises a jour techniques (securite, plugins)',
        'Ajout/modification de contenu',
        'Corrections de bugs',
        'Optimisation des performances',
        'Support technique',
        'Autre',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'maintenance_frequency',
      type: 'multiple_choice',
      question: 'Frequence de maintenance souhaitee ?',
      options: [
        'Ponctuelle (une fois)',
        'Mensuelle',
        'Trimestrielle',
        'En continu (support regulier)',
      ],
      required: true,
      category: 'general',
    },
    {
      id: 'current_tech',
      type: 'text',
      question: 'Connaissez-vous la technologie utilisee ?',
      placeholder: 'Ex: WordPress, Shopify, React...',
      required: false,
      category: 'technical',
    },
    {
      id: 'hosting_access',
      type: 'yes_no',
      question: 'Avez-vous acces a l\'hebergement ?',
      required: true,
      category: 'technical',
    },
  ],
  autre: [
    {
      id: 'project_description',
      type: 'textarea',
      question: 'Decrivez votre projet en detail',
      placeholder: 'Expliquez ce que vous souhaitez realiser...',
      required: true,
      category: 'general',
    },
    {
      id: 'project_goals',
      type: 'textarea',
      question: 'Quels sont vos objectifs ?',
      placeholder: 'Ex: Augmenter les ventes, ameliorer l\'experience utilisateur...',
      required: true,
      category: 'general',
    },
    {
      id: 'target_users',
      type: 'textarea',
      question: 'Qui sont vos utilisateurs cibles ?',
      placeholder: 'Ex: Entreprises, particuliers, age, localisation...',
      required: true,
      category: 'general',
    },
    {
      id: 'inspiration',
      type: 'textarea',
      question: 'Avez-vous des exemples ou inspirations ?',
      placeholder: 'URLs de sites ou applications que vous aimez...',
      required: false,
      category: 'design',
    },
    {
      id: 'constraints',
      type: 'textarea',
      question: 'Y a-t-il des contraintes particulieres ?',
      placeholder: 'Ex: Budget limite, delai court, technologies imposees...',
      required: false,
      category: 'general',
    },
  ],
}

// Labels pour les types de projets
export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  site_vitrine: 'Site Vitrine',
  ecommerce: 'E-commerce',
  application_web: 'Application Web',
  application_mobile: 'Application Mobile',
  refonte: 'Refonte de Site',
  maintenance: 'Maintenance',
  autre: 'Autre',
}

// Descriptions des types de projets
export const PROJECT_TYPE_DESCRIPTIONS: Record<ProjectType, string> = {
  site_vitrine: 'Site web pour presenter votre entreprise, vos services ou produits',
  ecommerce: 'Boutique en ligne pour vendre vos produits',
  application_web: 'Application web sur mesure pour vos besoins specifiques',
  application_mobile: 'Application iOS et/ou Android',
  refonte: 'Moderniser un site web existant',
  maintenance: 'Maintenance et support d\'un site existant',
  autre: 'Autre type de projet web',
}
